<template>
    <div class="land-plot-modal-top">
        <div class="land-plot-modal-top__slider">
            <SliderGallery v-if="landPlot.plot && landPlot.plot.images.length" :images="landPlot.plot.images"
                mode="plot" />

            <div v-else class="land-plot-modal-top__slider-placeholder"></div>

            <LandStatus class="land-plot-modal-top__status" :landPlotStatus="landPlot.plot.status" />
        </div>
        <div class="land-plot modal-top__info">
            <h2 class="land-plot-modal-top__info-title">
                <span v-if="landPlot.house">Проект </span>
                <span class="land-plot-modal-top__info-title-hl">{{
                    landPlot.plot.name
                }}</span>{{
                        props.landPlot.plot.street.name
                            ? `, ${props.landPlot.plot.street.name}`
                            : ''
                    }}{{
                    landPlot.plot.houseNumber
                        ? `, ${landPlot.plot.houseNumber}`
                        : ''
                }}
            </h2>

            <p v-if="landPlot.house" class="land-plot-modal-top__info-description">
                {{ landPlot.house.descriptionOut }}
            </p>

            <div v-show="landPlot.plot.action &&
                !(landPlot.plot.status === 'occupied')
                " class="land-plot-modal-top__info-promotion">
                <h3 class="land-plot-modal-top__info-promotion-title">
                    <GiftIcon class="land-plot-modal-top__info-promotion-icon" />
                    <span class="land-plot-modal-top__info-promotion-title-text">
                        Акция</span>
                </h3>

                <p class="land-plot-modal-top__info-promotion-main">
                    {{ landPlot.plot.action }}
                </p>
            </div>

            <div v-show="landPlot.plot.discount.value" class="land-plot-modal-top__info-promotion">
                <h3 class="land-plot-modal-top__info-promotion-title">
                    <PercentIcon class="land-plot-modal-top__info-promotion-icon" />

                    <span class="land-plot-modal-top__info-promotion-title-text">
                        Скидка {{ landPlot.plot.discount.value }}%</span>
                </h3>

                <p class="land-plot-modal-top__info-promotion-main">
                    {{ landPlot.plot.discount.description }}
                </p>
            </div>

            <div v-if="isSold || landPlot.plot.cost || landPlot.plot.newCost" class="land-plot-modal-top__info-price">
                <div v-if="isSold" class="land-plot-modal-top__info-price-set">
                    <div class="land-plot-modal-top__info-price-value land-plot-modal-top__info-price-value--sold">
                        Продано
                    </div>
                </div>

                <template v-else>
                    <div v-if="landPlot.plot.cost && landPlot.plot.cost === 1"
                        class="land-plot-modal-top__info-price-set">
                        <div class="land-plot-modal-top__info-price-value">
                            Скоро в продаже
                        </div>
                    </div>
                    <div v-if="landPlot.plot.cost && landPlot.plot.cost !== 1"
                        class="land-plot-modal-top__info-price-set">
                        <div class="land-plot-modal-top__info-price-label" v-html="`Цена с ${switchCostLabel(landPlot.plot.cost, [
                            `<b
                                >чистовой отделкой</b
                            >`,
                            `отделкой <b>Black box</b>`,
                        ])}`
                            "></div>
                        <div class="land-plot-modal-top__info-price-value">
                            {{ `${formatedCost(landPlot.plot.cost)} ₽` }}
                        </div>
                    </div>

                    <div v-if="landPlot.plot.newCost" class="land-plot-modal-top__info-price-set">
                        <div class="land-plot-modal-top__info-price-label" v-html="`Цена с ${switchCostLabel(
                            landPlot.plot.newCost,
                            [
                                `<b
                                >чистовой отделкой</b
                            >`,
                                `отделкой <b>White box</b>`,
                            ],
                        )}`
                            "></div>
                        <div class="land-plot-modal-top__info-price-value">
                            {{
                                `${formatedCost(landPlot.plot.newCost || 0)} ₽`
                            }}
                        </div>
                    </div>
                </template>
            </div>

            <div class="land-plot-modal-top__info-additional" v-if="landPlot.plot.additional">
                <p class="land-plot-modal-top__info-additional-txt text-ll text-simple-light ff-raleway">
                    {{ landPlot.plot.additional }}
                </p>
            </div>

            <div class="land-plot-modal-top__info-actions">
                <CustomButton :disabled="isSold ? 'disabled' : null" size="lg" theme="warning" tag="a"
                    href="#popup:book" aria-haspopup="dialog" @click="onBookButtonClick">Забронировать</CustomButton>
            </div>

            <div class="land-plot-modal-top__info-cadastral" v-if="cadastralNumber">
                <p class="land-plot-modal-top__info-cadastral-label">
                    Кадастровый номер
                </p>

                <p class="land-plot-modal-top__info-cadastral-value" @click="openCadastralPage()">
                    <span class="land-plot-modal-top__info-cadastral-number">
                        {{ cadastralNumber }}
                    </span>

                    <ChevronLeftIcon class="land-plot-modal-top__info-cadastral-icon" />
                </p>
            </div>

            <CustomList :list="listTop" theme="odd" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from 'vue';
import { cardSettingsStore } from '@/store/modules/CardSettings';
import CustomButton from '@/components/commons/CustomControls/CustomButton.vue';
import CustomList from '@/components/commons/CustomList.vue';
import PercentIcon from '@/components/commons/icons/PercentIcon.vue';
import GiftIcon from '@/components/commons/icons/GiftIcon.vue';
import SliderGallery from '@/components/slider/SliderGallery.vue';
import ChevronLeftIcon from '@/components/commons/icons/ChevronLeftIcon.vue';
import LandStatus from '@/components/commons/LandStatus.vue';
import { cashSpacer } from '@/helpers/cashHelpers';
import { switchCostLabel } from '@/helpers/costHelpers';
import { ILandPlot } from '@/types/LandPLot';
import { CustomListItem } from '@/types/common/CustomList';

const props = defineProps<{
    landPlot: ILandPlot;
}>();

const emits = defineEmits(['close']);

const BOOK_BUTTON_SELECTOR = '[href="#popup:book"]';

function formatedCost(price: number) {
    return cashSpacer(price, ' ');
}

const cadastralNumber = computed(() => {
    let result = props.landPlot.plot.cadastralNumber;
    const isNumber = /\d/.test(result);

    if (!isNumber) {
        result = '';
    }

    return result;
});

const listTop = computed<Array<CustomListItem>>(() => {
    const result = [
        {
            id: -1,
            title: 'Площадь участка,  соток',
            text: props.landPlot.plot.area
                ? props.landPlot.plot.area.toString()
                : '',
        },
        ...(props.landPlot.house?.area
            ? [
                {
                    id: -2,
                    title: 'Общая площадь дома, м²',
                    text: props.landPlot.house?.area
                        ? props.landPlot.house.area.toString()
                        : '',
                },
            ]
            : []),
    ];

    if (props.landPlot.house)
        props.landPlot.house.out.forEach(i =>
            result.push({
                id: i.id,
                title: i.name,
                text: i.value,
            }),
        );
    if (
        props.landPlot &&
        props.landPlot.plot &&
        props.landPlot.plot.parameters &&
        props.landPlot.plot.parameters.length
    )
        props.landPlot.plot.parameters.forEach(param => {
            if (param.name === 'Срок сдачи' && param.value) {
                result.push({
                    id: param.id || 0,
                    title: param.name,
                    text: param.value,
                });
            }
        });

    return result;
});

const isSold = computed(() => {
    return (
        (!props.landPlot.plot.cost && !props.landPlot.plot.newCost) ||
        props.landPlot.plot.status === 'occupied'
    );
});

function onBookButtonClick() {
    cardSettingsStore.LAND_PLOT_CARD_IN_MODAL_SET_SHOW_STATUS(false);

    const popupEl = document.querySelector(
        '[data-tooltip-hook="#popup:book"]',
    );

    if (!popupEl) return;

    const {
        plot: { houseNumber, name },
    } = props.landPlot;

    const houseInputEl = popupEl.querySelector(
        'input[name="house_number"]',
    ) as HTMLInputElement | null;
    const projectInputEl = popupEl.querySelector(
        'input[name="project_name"]',
    ) as HTMLInputElement | null;
    const actionInputEl = popupEl.querySelector(
        'input[name="action_name"]',
    ) as HTMLInputElement | null;

    if (houseInputEl) {
        houseInputEl.value = houseNumber || '';
    }

    if (projectInputEl) {
        projectInputEl.value = name || '';
    }

    if (actionInputEl) {
        actionInputEl.value = 'Забронировать';
    }
}

function openCadastralPage() {
    if (!cadastralNumber.value) return;
    const text = cadastralNumber.value.replace(/:/g, '%3A');
    const opened = cadastralNumber.value
        .split(':')
        .map(item => {
            if (item.startsWith('0')) item = item.substring(1);
            return item;
        })
        .join('%3A');
    const url = `https://pkk.rosreestr.ru/#/search/?text=${text}&type=1&opened=${opened}&inPoint=true`;

    window.open(url, '_blank');
}
</script>

<style lang="less">
.land-plot-modal-top {
    padding: 50px 60px 50px 50px;
    display: grid;
    grid-template-columns: 430px minmax(0, 1fr);
    gap: 34px;

    @media (max-width: @size-desktop-top) {
        display: block;
        padding: 20px;
    }

    // .land-plot-modal-top__info
    &__info {

        // .land-plot-modal-top__info-title
        &-title {
            margin-bottom: 15px;
            font-size: 30px;
            line-height: 34.5px;
            color: @dark-brown;

            @media (max-width: @size-desktop-top) {
                font-size: 24px;
                line-height: 27.6px;
                margin-bottom: 24px;
            }

            @media (max-width: @size-mobile-top) {
                margin-bottom: 16px;
            }

            &-hl {
                color: @warning;
            }
        }

        // .land-plot-modal-top__info-promotion
        &-promotion {
            background-color: @second-gray;
            padding: 6px;
            display: flex;
            gap: 20px;
            align-items: center;
            margin-bottom: 32px;

            @media (max-width: @size-desktop-top) {
                margin-bottom: 24px;
            }

            @media (max-width: @size-mobile-top) {
                display: block;
                margin-bottom: 16px;
            }

            // .land-plot-modal-top__info-promotion-title
            &-title {
                display: inline-flex;
                align-items: center;
                padding: 16px 19px;
                color: @success;
                gap: 5px;
                background-color: @simple-light;

                @media (max-width: @size-mobile-top) {
                    display: flex;
                    margin-bottom: 13px;
                }

                // .land-plot-modal-top__info-promotion-title-text
                &-text {
                    .text-1();

                    white-space: nowrap;

                    @media (max-width: @size-desktop-top) {
                        font-size: 14px;
                        line-height: 21px;
                    }
                }
            }

            // .land-plot-modal-top__info-promotion-main
            &-main {
                .text-3();

                width: 100%;
                color: @medium-dark-gray;

                @media (max-width: @size-desktop-top) {
                    .text-1-mobile();
                }
            }

            // .land-plot-modal-top__info-promotion-btn
            &-btn {
                cursor: pointer;
                text-decoration: underline;
                background-color: transparent;
                box-shadow: unset;
                outline: unset;
                border: unset;
                margin: 0;
                padding: 0;
            }

            // .land-plot-modal-top__info-promotion-icon
            &-icon {
                height: 25px;
            }
        }

        // .land-plot-modal-top__info-additional
        &-additional {
            padding: 20px 25px;
            border-radius: 10px;
            background-color: @success;
            margin-bottom: 30px;
        }

        // .land-plot-modal-top__info-cadastral
        &-cadastral {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 10px 20px;
            margin-bottom: 4px;

            @media (max-width: @size-mobile-top) {
                padding: 11px 16px;
            }

            // .land-plot-modal-top__info-cadastral-label
            &-label {
                .text-3();

                color: @medium-dark-gray;

                @media (max-width: @size-mobile-top) {
                    .text-3-mobile();
                }
            }

            // .land-plot-modal-top__info-cadastral-value
            &-value {
                .text-3();

                display: inline-flex;
                align-items: center;
                gap: 8px;
                cursor: pointer;
                color: @warning;

                @media (max-width: @size-mobile-top) {
                    gap: 7px;
                }
            }

            &-number {
                font-family: 'Open Sans', sans-serif;
                font-size: 14px;
                font-weight: 600;
                line-height: 18.9px;
                text-align: right;

                @media (max-width: @size-mobile-top) {
                    font-size: 12px;
                    line-height: 18px;
                }
            }

            &-icon {
                height: 8px;
                transform: rotate(180deg);
            }
        }

        // .land-plot-modal-top__info-subtitle
        &-subtitle {
            margin-bottom: 15px;
            font-size: 16px;
            font-weight: 500;
            line-height: 22.4px;
            color: @medium-dark-gray;

            @media (max-width: @size-desktop-top) {
                font-size: 14px;
                line-height: 19.6px;
                margin-bottom: 17px;
            }

            @media (max-width: @size-mobile-top) {
                margin-bottom: 15px;
            }
        }

        // .land-plot-modal-top__info-price
        &-price {
            display: flex;
            align-items: flex-end;
            margin-bottom: 15px;
            gap: 49px;

            @media (max-width: @size-desktop-top) {
                margin-bottom: 16px;
            }

            &-label {
                .text-3();

                margin-bottom: 8px;
                color: @dark;

                @media (max-width: @size-desktop-top) {
                    margin-bottom: 4px;
                }
            }

            &-value {
                font-size: 28px;
                line-height: 33.6px;
                color: @success;

                @media (max-width: @size-desktop-top) {
                    font-size: 24px;
                    line-height: 28.8px;
                }

                &--sold {
                    color: @medium-dark-gray;
                }
            }
        }

        // .land-plot-modal-top__info-actions
        &-actions {
            display: grid;
            grid-template-columns: minmax(0, 296px);
            margin-bottom: 26px;

            @media (max-width: @size-desktop-top) {
                display: block;
                margin-bottom: 24px;
            }

            @media (max-width: @size-mobile-top) {
                margin-bottom: 16px;
            }
        }

        &-description {
            .text-2();

            margin-bottom: 15px;
            color: @medium-dark-gray;

            @media (max-width: @size-desktop-top) {
                .text-3-mobile();

                margin-bottom: 24px;
            }
        }
    }

    &__slider {
        position: relative;

        @media (max-width: @size-desktop-top) {
            margin-bottom: 20px;
        }
    }

    &__slider-placeholder {
        height: 294px;
        background-color: rgba(@success, 0.5);

        @media (max-width: @size-desktop-top) {
            height: 409px;
        }

        @media (max-width: @size-mobile-top) {
            height: 205px;
        }
    }

    &__status {
        position: absolute;
        top: 14px;
        left: 14px;
        z-index: 1;

        @media (max-width: @size-desktop-top) {
            top: 10px;
            left: 10px;
        }
    }
}
</style>
